(function (root) {
  'use strict';

  if (root.document && root.AMPromotions) {
    root.AMPromotions.init();
    return;
  }

  const TAG_PATTERN = /^am-promo:([a-z0-9]+(?:-[a-z0-9]+)*):(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(Z|([+-])(\d{2}):(\d{2}))$/;
  const CACHE_MS = 30000;
  const REFRESH_MS = 60000;
  const TIMEOUT_MS = 8000;

  function parseTag(tag) {
    if (typeof tag !== 'string') return null;
    const match = TAG_PATTERN.exec(tag);
    if (!match) return null;
    const [, key, yearText, monthText, dayText, hourText, minuteText, secondText, zone, sign, offsetHourText, offsetMinuteText] = match;
    const [year, month, day, hour, minute, second] = [yearText, monthText, dayText, hourText, minuteText, secondText].map(Number);
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (month < 1 || month > 12 || day < 1 || day > days[month - 1] || hour > 23 || minute > 59 || second > 59) return null;
    const offsetHours = zone === 'Z' ? 0 : Number(offsetHourText);
    const offsetMinutes = zone === 'Z' ? 0 : Number(offsetMinuteText);
    if (offsetHours > 23 || offsetMinutes > 59) return null;
    // setUTCFullYear preserves ISO years 0000–0099, unlike Date.UTC's 1900 offset.
    const local = new Date(0);
    local.setUTCFullYear(year, month - 1, day);
    local.setUTCHours(hour, minute, second, 0);
    const offset = (offsetHours * 60 + offsetMinutes) * 60000 * (sign === '-' ? -1 : 1);
    const endTime = local.getTime() - offset;
    if (!Number.isFinite(endTime)) return null;
    return { tag, key, end: tag.slice('am-promo:'.length + key.length + 1), endTime };
  }

  function isActive(tag, now = Date.now()) {
    const promotion = parseTag(tag);
    return Boolean(promotion && Number.isFinite(now) && now < promotion.endTime);
  }

  function earliestPromotion(tags, now = Date.now()) {
    if (!Array.isArray(tags)) return null;
    return tags.reduce((earliest, tag) => {
      const candidate = parseTag(tag);
      if (!candidate || !Number.isFinite(now) || candidate.endTime <= now) return earliest;
      return !earliest || candidate.endTime < earliest.endTime ? candidate : earliest;
    }, null);
  }

  function formatRemaining(endTime, now = Date.now()) {
    const seconds = Math.max(0, Math.ceil((endTime - now) / 1000));
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor(seconds / 3600) % 24;
    const minutes = Math.floor(seconds / 60) % 60;
    const pad = value => String(value).padStart(2, '0');
    return `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds % 60)}s`;
  }

  function createRuntime(env) {
    const now = typeof env.now === 'function' ? env.now : () => Date.now();
    const later = env.setTimeout.bind(env);
    const cancel = env.clearTimeout.bind(env);
    const every = env.setInterval.bind(env);
    const stopEvery = env.clearInterval.bind(env);
    const cache = new Map();
    const controllers = new Map();
    let listening = false;

    function sameOriginURL(url) {
      if (typeof url !== 'string' || !url) throw new Error('Missing promotion URL');
      const base = env.location && env.location.href;
      const parsed = new URL(url, base || undefined);
      if (!/^https?:$/.test(parsed.protocol) || (base && parsed.origin !== new URL(base).origin)) throw new Error('Invalid promotion URL');
      return parsed.href;
    }

    async function request(url, format) {
      const controller = new env.AbortController();
      let timeout;
      const timedOut = new Promise((resolve, reject) => {
        timeout = later(() => {
          controller.abort();
          reject(new Error('Promotion request timed out'));
        }, TIMEOUT_MS);
      });
      try {
        return await Promise.race([
          (async () => {
            const response = await env.fetch(sameOriginURL(url), {
              credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
              headers: { Accept: format === 'json' ? 'application/json' : 'text/html' }
            });
            if (!response.ok) throw new Error('Promotion request failed');
            // An absent collection tag redirects to the unfiltered collection.
            // The exact current_tags check below is mandatory even after a 200.
            return format === 'json' ? response.json() : response.text();
          })(),
          timedOut
        ]);
      } finally {
        cancel(timeout);
      }
    }

    function statusFromHTML(html, tag) {
      if (typeof html !== 'string') return false;
      const document = new env.DOMParser().parseFromString(html, 'text/html');
      const nodes = document.querySelectorAll('script[type="application/json"][data-am-promotion-status]');
      if (nodes.length !== 1) return false;
      try {
        const status = JSON.parse(nodes[0].textContent);
        return Boolean(status && Array.isArray(status.tags) && status.tags.includes(tag) && Number.isInteger(status.count) && status.count > 0);
      } catch (_) {
        return false;
      }
    }

    function notify(tag, url, active) {
      if (typeof env.CustomEvent === 'function' && typeof env.dispatchEvent === 'function') {
        env.dispatchEvent(new env.CustomEvent('am:promotion-status', { detail: { tag, url, active } }));
      }
    }

    async function checkTag(tag, url, options = {}) {
      if (!isActive(tag, now())) return false;
      let resolved;
      try { resolved = sameOriginURL(url); } catch (_) { return false; }
      const key = `${tag}\n${resolved}`;
      const previous = cache.get(key);
      if (!options.force && previous) {
        if (previous.pending) return previous.pending;
        if (previous.expires > now()) return previous.value;
      }
      const entry = { value: false, expires: 0, pending: null };
      cache.set(key, entry);
      entry.pending = (async () => {
        let active = false;
        try {
          active = statusFromHTML(await request(resolved, 'text'), tag) && isActive(tag, now());
        } catch (_) { /* Missing, stale, or unreachable status stays hidden. */ }
        entry.value = active;
        entry.expires = now() + CACHE_MS;
        entry.pending = null;
        if (cache.get(key) !== entry) {
          // A force refresh or invalidation supersedes this response, including
          // for callers still awaiting it; stale success must not revive a sale.
          const latest = cache.get(key);
          if (!latest || !isActive(tag, now())) return false;
          return latest.pending || (latest.expires > now() && latest.value);
        }
        if (!previous || previous.value !== active) notify(tag, resolved, active);
        return active;
      })();
      return entry.pending;
    }

    function invalidateTag(tag, url) {
      if (tag === undefined) { cache.clear(); return; }
      let resolved;
      if (url !== undefined) {
        try { resolved = sameOriginURL(url); } catch (_) { return; }
      }
      for (const key of cache.keys()) {
        if (key.startsWith(`${tag}\n`) && (!resolved || key === `${tag}\n${resolved}`)) cache.delete(key);
      }
    }

    function createProductController(element) {
      let tags = [];
      let trusted = true;
      let destroyed = false;
      let pending = null;
      let displayedTag = null;
      const label = element.querySelector('[data-end-label]');
      const countdown = element.querySelector('[data-countdown]');
      try {
        const initial = JSON.parse(element.dataset.tags || '[]');
        if (Array.isArray(initial) && initial.every(tag => typeof tag === 'string')) tags = initial;
      } catch (_) { /* Malformed initial data stays hidden until refreshed. */ }
      const dateFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Toronto', year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
      });

      function tick() {
        if (destroyed) return;
        const promotion = trusted ? earliestPromotion(tags, now()) : null;
        if (!promotion || !label || !countdown) {
          element.hidden = true;
          displayedTag = null;
          if (countdown) countdown.textContent = '';
          return;
        }
        if (displayedTag !== promotion.tag) {
          label.textContent = `Promotion ends ${dateFormatter.format(new Date(promotion.endTime))}`;
          displayedTag = promotion.tag;
        }
        countdown.textContent = formatRemaining(promotion.endTime, now());
        element.hidden = false;
      }

      async function refresh() {
        if (destroyed) return false;
        if (pending) return pending;
        pending = (async () => {
          try {
            const product = await request(element.dataset.productUrl, 'json');
            if (!product || !Array.isArray(product.tags) || !product.tags.every(tag => typeof tag === 'string')) throw new Error('Invalid product tags');
            if (!destroyed) { tags = product.tags; trusted = true; }
          } catch (_) {
            if (!destroyed) { tags = []; trusted = false; }
          } finally {
            pending = null;
            tick();
          }
          return !destroyed && !element.hidden;
        })();
        return pending;
      }

      tick();
      const tickTimer = every(tick, 1000);
      const refreshTimer = every(() => {
        if (!env.document || env.document.visibilityState !== 'hidden') refresh();
      }, REFRESH_MS);
      const controller = {
        refresh, tick,
        destroy() {
          destroyed = true;
          stopEvery(tickTimer);
          stopEvery(refreshTimer);
          element.hidden = true;
          controllers.delete(element);
        }
      };
      controllers.set(element, controller);
      refresh();
      return controller;
    }

    function init(scope = env.document) {
      if (!scope) return;
      const nodes = [...scope.querySelectorAll('[data-am-product-promotion]')];
      if (scope.matches && scope.matches('[data-am-product-promotion]')) nodes.unshift(scope);
      nodes.forEach(element => {
        if (!controllers.has(element)) createProductController(element);
      });
      if (!listening && env.document) {
        listening = true;
        const refreshVisibleControllers = () => {
          for (const [element, controller] of controllers) {
            if (!element.isConnected) { controller.destroy(); continue; }
            controller.tick();
            if (env.document.visibilityState !== 'hidden') controller.refresh();
          }
        };
        env.document.addEventListener('visibilitychange', refreshVisibleControllers);
        if (typeof env.addEventListener === 'function') env.addEventListener('pageshow', refreshVisibleControllers);
        env.document.addEventListener('shopify:section:load', event => init(event.target));
        env.document.addEventListener('shopify:section:unload', event => {
          for (const [element, controller] of controllers) {
            if (event.target.contains(element)) controller.destroy();
          }
        });
      }
    }

    return { parseTag, isActive, earliestPromotion, formatRemaining, checkTag, invalidateTag, init, createProductController };
  }

  const api = createRuntime(root);
  if (typeof module === 'object' && module.exports) module.exports = { ...api, createRuntime };
  if (root.document) {
    root.AMPromotions = api;
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', () => api.init(), { once: true });
    else api.init();
  }
})(typeof window === 'object' ? window : globalThis);
